import { usePostLogout } from '@repo/api/auth/usePostLogout';
import { cn } from '@repo/lib/cn';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { BellOffIcon, BellRingIcon, LockIcon, LogOutIcon, UploadCloud, UserIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import SettingOptionHeader from '@/components/features/settings/SettingOptionHeader';
import { ThemeRadioGroup } from '@/components/features/settings/ThemeRadioGroup';
import UserAvatarEditor from '@/components/features/user/UserAvatarEditor';
import { Avatar, AvatarEdit, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useManageNotifications } from '@/hooks/notifications';
import { useCredentials, useRemoveCredentials } from '@/store/auth-store';

export default function SettingsPage() {
  const navigate = useNavigate();
  const removeCredentials = useRemoveCredentials();
  const { name, surname, lvl, avatarURL, accountType } = useCredentials();
  const userRole = getUserLvl(lvl, accountType);
  const { mutate, isPending } = usePostLogout();
  const { isGranted, areNotificationsSupported, enableNotifications, disableNotifications } = useManageNotifications();

  const [openCropper, setOpenCropper] = useState<boolean>(false);

  const [notificationValue, setNotificationValue] = useState<'notifications-off' | 'notifications-on'>(isGranted ? 'notifications-on' : 'notifications-off');

  const onNotificationValueChange = () => {
    if (isGranted) {
      setNotificationValue('notifications-off');
      disableNotifications()
        .then(() => setNotificationValue('notifications-off'))
        .catch(() => setNotificationValue('notifications-on'));
    } else {
      setNotificationValue('notifications-on');
      enableNotifications()
        .then(() => setNotificationValue('notifications-on'))
        .catch(() => setNotificationValue('notifications-off'));
    }
  };

  const handleLogOut = () => {
    mutate();
    removeCredentials();
    navigate('/login');
  };

  const handleClickPasswordChange = () => {
    navigate('/auth/passwordChange/');
  };

  return (
    <>
      <UserAvatarEditor open={openCropper} onOpenChange={setOpenCropper} />
      <div className={'base-direction-col'}>
        <div className={'border-border-primary md:bg-background flex w-full grow flex-col p-0 md:rounded-2xl md:border'}>
          <div className={'flex items-center gap-4 py-6 md:px-3'}>
            <Avatar size={'lg'}>
              <AvatarImage src={avatarURL ?? undefined} alt="avatar" />
              <AvatarFallback>
                <UserIcon className={'size-4'} />
              </AvatarFallback>
            </Avatar>
            <div className={'space-y-0.5'}>
              <h5 className={'text-lg font-semibold'}>{name + ' ' + surname}</h5>
              <p className={'text-text-tertiary text-sm'}>{userRole}</p>
            </div>
          </div>
          <div className={'border-border-primary flex flex-col items-center gap-4 py-6 md:flex-row md:border-t md:px-4'}>
            <SettingOptionHeader className={'w-full md:w-54'} title={'Zmień motyw'} description={'Wybierz swój motyw.'} />
            <div className={'flex w-full gap-4'}>
              <ThemeRadioGroup />
            </div>
          </div>
          <div className={'border-border-primary flex flex-col items-center gap-4 py-6 md:flex-row md:border-t md:px-4'}>
            <SettingOptionHeader className={'w-full md:w-56'} title={'Zmień Awatar'} description={'Naciśnij, i zmień swojego awatara na jakiego tylko chcesz.'} />
            <div className={'flex items-center gap-4'}>
              <AvatarEdit className={'size-20'} onClick={() => setOpenCropper(true)}>
                <AvatarImage src={avatarURL ?? undefined} alt="avatar" />
              </AvatarEdit>
              <Button variant={'outline'} onClick={() => setOpenCropper(true)}>
                <UploadCloud />
                Dodaj zdjęcie
              </Button>
            </div>
          </div>
          <div className={'border-border-primary flex flex-col items-center gap-4 py-6 md:flex-row md:border-t md:px-4'}>
            <SettingOptionHeader className={'w-full md:w-56'} title={'Zmień hasło'} description={'Naciśnij przycisk aby zmienić swoje hasło.'} />
            <Button onClick={handleClickPasswordChange} variant={'outline'} className="w-fit">
              <LockIcon />
              Zmień hasło
            </Button>
          </div>
          <div className={cn('border-border-primary flex flex-col items-center gap-4 py-6 md:flex-row md:border-t md:px-4', !areNotificationsSupported && 'hidden')}>
            <SettingOptionHeader className={'w-full md:w-56'} title={'Powiadomienia'} description={'Pozostań na bieżąco! Wyraź zgodę na powiadomienia, aby otrzymywać najnowsze informacje.'} />
            <Tabs value={notificationValue} onValueChange={onNotificationValueChange} className={'shrink-0'}>
              <TabsList>
                <TabsTrigger value={'notifications-off'}>
                  <BellOffIcon />
                  Off
                </TabsTrigger>
                <TabsTrigger value={'notifications-on'}>
                  <BellRingIcon />
                  On
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className={'border-border-primary flex flex-col items-center gap-4 py-6 md:flex-row md:border-t md:px-4'}>
            <SettingOptionHeader className={'w-full md:w-54'} title={'Wyloguj się'} description={'Naciśnij, przycisk aby wylogować sie z konta.'} />
            <Button disabled={isPending} onClick={handleLogOut} variant={'outline'} variantColor={'destructive'} className={'w-fit'}>
              <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                <LogOutIcon />
                Wyloguj się
              </LoadingSwap>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
